/**
 * HealthRing — tiny SVG ring showing tenant health score (0-100).
 * Color-coded by status so the admin gets a "traffic light" at a glance.
 *
 *   green  → score ≥ 70  (healthy)
 *   amber  → 40 ≤ score < 70  (watchlist)
 *   red    → score < 40  (unhealthy)
 *   new    → no signals yet (brand-new tenant)
 *
 * Hover tooltip shows the underlying signals so the admin can diagnose
 * without clicking into the tenant.
 */
export default function HealthRing({ health, size = 34, stroke = 3 }) {
  if (!health) return null;
  const score = Math.max(0, Math.min(100, Number(health.score) || 0));
  const status = health.status || 'amber';

  const colors = {
    green: { ring: '#22c55e', bg: 'rgba(34,197,94,0.15)', text: '#86efac' },
    amber: { ring: '#f59e0b', bg: 'rgba(245,158,11,0.15)', text: '#fbbf24' },
    red: { ring: '#ef4444', bg: 'rgba(239,68,68,0.15)', text: '#fca5a5' },
    new: { ring: '#64748b', bg: 'rgba(100,116,139,0.15)', text: '#cbd5e1' },
  };
  const c = colors[status] || colors.amber;

  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;

  const tooltip =
    status === 'new'
      ? 'New tenant — no activity yet'
      : `Score ${score}/100 · ${health.active_7d || 0} active (7d) · ${health.deposits_7d || 0} deposits (7d) · ${health.pending_withdrawals || 0} pending WD`;

  return (
    <div
      className="relative flex items-center justify-center shrink-0"
      title={tooltip}
      data-testid={`tenant-health-ring-${status}`}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.08)"
                strokeWidth={stroke} fill="transparent" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={c.ring}
                strokeWidth={stroke} fill="transparent"
                strokeDasharray={`${dash} ${circ - dash}`}
                strokeLinecap="round"
                style={{ transition: 'stroke-dasharray 500ms ease-out' }} />
      </svg>
      <span
        className="absolute text-[10px] font-bold tabular-nums"
        style={{ color: c.text }}
      >
        {status === 'new' ? '–' : score}
      </span>
    </div>
  );
}
